import React, { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/theme-provider";
import "@/App.css";
import { LocaleProvider, useLocale } from "@/context/LocaleContext";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectPage from "@/pages/ProjectPage";
import JoinInvitePage from "@/pages/JoinInvitePage";
import HelpPage from "@/pages/HelpPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { PROJECTS_LIST_PATH } from "@/lib/routes";
import { readClientPreferences } from "@/lib/clientPreferences";
import { UserPreferencesSync } from "@/components/UserPreferencesSync";

/** Utelämna schema → https prependas (t.ex. api.domän.se). Tom sträng ⇒ `/api` (samma origin, kräver dev-proxy om backend finns annan host). */
function normalizeBackendBase(u) {
  if (u == null || typeof u !== "string") return "";
  const t = u.trim().replace(/\/+$/, "");
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}
const BACKEND_URL = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

/** Cross-origin API: httpOnly auth-cookies är inte läsbara via document.cookie — dubbelläge mot CSRF-header. */
let apiCsrfToken = "";
function setApiCsrfToken(v) {
  apiCsrfToken = typeof v === "string" && v ? v : "";
}

async function ensureApiCsrfToken() {
  if (apiCsrfToken) return apiCsrfToken;
  const cookieToken = getCookie("csrf_token");
  if (cookieToken) {
    setApiCsrfToken(cookieToken);
    return cookieToken;
  }
  const { data } = await axios.get(`${API}/auth/csrf`, { __isCsrfBootstrap: true });
  const token = data?.csrf_token || "";
  if (token) setApiCsrfToken(token);
  return token;
}

function stripCsrfFromUserPayload(data) {
  if (!data || typeof data !== "object") return data;
  const { csrf_token: _c, ...rest } = data;
  return rest;
}

function applyAuthPayload(data) {
  if (data && typeof data === "object" && data.csrf_token) {
    setApiCsrfToken(data.csrf_token);
  }
  return stripCsrfFromUserPayload(data);
}

function getCookie(name) {
  const raw = document.cookie || "";
  const parts = raw.split(";").map((p) => p.trim());
  for (const p of parts) {
    if (!p) continue;
    const eq = p.indexOf("=");
    const k = eq >= 0 ? p.slice(0, eq) : p;
    const v = eq >= 0 ? p.slice(eq + 1) : "";
    if (k === name) return decodeURIComponent(v);
  }
  return "";
}

axios.interceptors.request.use((config) => {
  const method = String(config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrf = apiCsrfToken || getCookie("csrf_token");
    if (!config.headers) config.headers = {};
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

let refreshInFlight = null;
function emitAuthLogout() {
  try {
    window.dispatchEvent(new Event("auth:logout"));
  } catch {}
}

axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    if (status !== 401) throw err;
    if (cfg._retry) throw err;
    if (cfg.__isRefresh) throw err;
    if (cfg.__isCsrfBootstrap) throw err;

    cfg._retry = true;
    try {
      if (!refreshInFlight) {
        refreshInFlight = ensureApiCsrfToken().then(() =>
          axios.post(`${API}/auth/refresh`, {}, { __isRefresh: true }).then((res) => {
            const tok = res?.data?.csrf_token;
            if (tok) setApiCsrfToken(tok);
            return res;
          }),
        );
      }
      await refreshInFlight;
      return axios(cfg);
    } catch (e) {
      emitAuthLogout();
      throw err;
    } finally {
      refreshInFlight = null;
    }
  },
);

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out
  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then((r) => setUser(applyAuthPayload(r.data)))
      .catch(() => {
        setUser(false);
      });
  }, []);
  useEffect(() => {
    const onLogout = () => {
      setApiCsrfToken("");
      setUser(false);
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    const userOut = applyAuthPayload(data);
    setUser(userOut);
    return userOut;
  };
  const register = async (name, email, password) => {
    const prefs = readClientPreferences();
    const { data } = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      ...prefs,
    });
    const userOut = applyAuthPayload(data);
    setUser(userOut);
    return userOut;
  };
  const forgotPassword = async (email) => {
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  };
  const resetPassword = async (token, password) => {
    const { data } = await axios.post(`${API}/auth/reset-password`, { token, password });
    return data;
  };
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setApiCsrfToken("");
    setUser(false);
  };
  const refreshUser = async () => {
    const { data } = await axios.get(`${API}/auth/me`);
    setUser(applyAuthPayload(data));
    return applyAuthPayload(data);
  };
  return (
    <AuthContext.Provider value={{ user, login, register, forgotPassword, resetPassword, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const { t } = useLocale();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--accent)]">{t("common.loading")}</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LocaleProvider>
        <AuthProvider>
          <UserPreferencesSync />
          <BrowserRouter>
            <Toaster position="top-right" />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/join/:inviteCode" element={<JoinInvitePage />} />
              <Route path="/" element={<ProtectedRoute><Navigate to={PROJECTS_LIST_PATH} replace /></ProtectedRoute>} />
              <Route path={PROJECTS_LIST_PATH} element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/project/:id" element={<ProtectedRoute><ProjectPage /></ProtectedRoute>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LocaleProvider>
    </ThemeProvider>
  );
}

export default App;
